// SPDX-License-Identifier: MIT
// DuckDB WASM Service - Singleton pattern for Atlas Viewer
// Based on patterns from Kepler.gl's @kepler.gl/duckdb package

import * as duckdb from '@duckdb/duckdb-wasm';
import { tableToIPC, type Table as ArrowTable } from 'apache-arrow';
import type { DuckDBStatus, QueryResult, SqlColumn, QueryExecutionOptions } from '../types';
import { DEFAULT_DISPLAY_ROW_LIMIT } from '../types';

// Vite-specific imports for WASM and worker files
// The ?url suffix tells Vite to return the resolved URL after bundling/hashing
// This is REQUIRED for production builds - using new URL() with import.meta.url
// works in dev but fails in production because the paths aren't properly resolved.
// See: https://github.com/duckdb/duckdb-wasm/blob/main/packages/duckdb-wasm/README.md#vite
import duckdb_wasm_mvp from '@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm?url';
import duckdb_worker_mvp from '@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js?url';
import duckdb_wasm_eh from '@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url';
import duckdb_worker_eh from '@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url';

/**
 * Singleton storage - use globalThis to survive code-splitting/module duplication.
 * In production builds, service can be imported from multiple chunks. Storing state
 * in module-scoped variables causes duplicate DuckDB instances (one per chunk).
 * Using globalThis ensures single instance across all import paths.
 *
 * Pattern matches official DuckDB-WASM examples:
 * https://github.com/duckdb/duckdb-wasm/blob/main/examples/plain-html/index.html
 */
interface DuckDBGlobalState {
  instance: DuckDBService | null;
  initializationPromise: Promise<DuckDBService> | null;
}

declare global {
  var __duckdb_atlas_viewer__: DuckDBGlobalState | undefined;
}

const getGlobalState = (): DuckDBGlobalState => {
  if (!globalThis.__duckdb_atlas_viewer__) {
    globalThis.__duckdb_atlas_viewer__ = {
      instance: null,
      initializationPromise: null,
    };
  }
  return globalThis.__duckdb_atlas_viewer__;
};

/**
 * DuckDB WASM Service - Provides a singleton interface to DuckDB-WASM
 *
 * Usage:
 *   const db = await DuckDBService.getInstance();
 *   const result = await db.executeQuery('SELECT * FROM my_table LIMIT 10');
 */
export class DuckDBService {
  private db: duckdb.AsyncDuckDB | null = null;
  private conn: duckdb.AsyncDuckDBConnection | null = null;
  private status: DuckDBStatus = 'uninitialized';
  private initError: Error | null = null;
  /** Map of registered tables and their source dataset IDs */
  private tableRegistry: Map<string, { datasetId: string; isSecure: boolean }> = new Map();

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton DuckDB service instance
   * Initializes on first call, returns cached instance on subsequent calls
   */
  static async getInstance(): Promise<DuckDBService> {
    const state = getGlobalState();

    if (state.instance && state.instance.status === 'ready') {
      return state.instance;
    }

    if (state.initializationPromise) {
      return state.initializationPromise;
    }

    state.instance = new DuckDBService();
    state.initializationPromise = state.instance
      .initialize()
      .then(() => {
        const s = getGlobalState();
        s.initializationPromise = null;
        return s.instance!;
      })
      .catch(err => {
        const s = getGlobalState();
        s.initializationPromise = null;
        throw err;
      });

    return state.initializationPromise;
  }

  /**
   * Get the current status of the DuckDB service
   */
  static getStatus(): DuckDBStatus {
    const state = getGlobalState();
    return state.instance?.status ?? 'uninitialized';
  }

  /**
   * Reset the singleton instance (mainly for testing)
   */
  static async reset(): Promise<void> {
    const state = getGlobalState();
    if (state.instance) {
      await state.instance.close();
      state.instance = null;
    }
    state.initializationPromise = null;
  }

  /**
   * Initialize DuckDB-WASM with appropriate bundles
   */
  private async initialize(): Promise<void> {
    if (this.status === 'ready') return;
    if (this.status === 'initializing') {
      throw new Error('DuckDB is already initializing');
    }

    this.status = 'initializing';

    try {
      // Use Vite's ?url imports for proper production bundling
      // These imports are resolved at build time and work correctly in both dev and prod
      const MANUAL_BUNDLES: duckdb.DuckDBBundles = {
        mvp: {
          mainModule: duckdb_wasm_mvp,
          mainWorker: duckdb_worker_mvp,
        },
        eh: {
          mainModule: duckdb_wasm_eh,
          mainWorker: duckdb_worker_eh,
        },
      };

      // Select the appropriate bundle based on browser capabilities
      const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);

      // Create worker and instantiate database
      const worker = new Worker(bundle.mainWorker!);
      const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
      this.db = new duckdb.AsyncDuckDB(logger, worker);

      await this.db.instantiate(bundle.mainModule);

      // Load spatial extension for geometry operations
      await this.db.open({
        path: ':memory:',
        query: {
          castBigIntToDouble: true,
        },
      });

      // Create connection
      this.conn = await this.db.connect();

      // Install and load spatial extension
      await this.conn.query(`INSTALL spatial; LOAD spatial;`);

      this.status = 'ready';
      console.log('[DuckDB] Service initialized successfully');
    } catch (error) {
      this.status = 'error';
      this.initError = error instanceof Error ? error : new Error(String(error));
      console.error('[DuckDB] Initialization failed:', error);
      throw this.initError;
    }
  }

  /**
   * Execute a SQL query and return results
   */
  async executeQuery(sql: string, _options: QueryExecutionOptions = {}): Promise<QueryResult> {
    if (this.status !== 'ready' || !this.conn) {
      throw new Error('DuckDB is not ready. Current status: ' + this.status);
    }

    const startTime = performance.now();

    try {
      // Execute the query
      const result = await this.conn.query(sql);
      const executionTimeMs = performance.now() - startTime;

      // Extract column schema
      const schema = result.schema;
      const columns: SqlColumn[] = schema.fields.map(field => ({
        name: field.name,
        type: field.type.toString(),
        nullable: field.nullable,
      }));

      // Convert result to array of records (extract ALL rows from query result)
      const rows: Record<string, unknown>[] = [];
      const rowIterator = result.toArray();

      for (let i = 0; i < rowIterator.length; i++) {
        const row = rowIterator[i];
        const record: Record<string, unknown> = {};
        columns.forEach((col, idx) => {
          // Handle BigInt conversion
          // DuckDB-WASM StructRow uses toJSON() for data access
          const value = row.toJSON ? row.toJSON()[col.name] : (row[col.name] ?? row[idx]);
          record[col.name] = typeof value === 'bigint' ? Number(value) : value;
        });
        rows.push(record);
      }

      // Determine source datasets from tables referenced in query
      const sourceDatasetIds = this.getSourceDatasetIds(sql);

      // Compute display slice for UI rendering (limit to DEFAULT_DISPLAY_ROW_LIMIT)
      const displayRows = rows.slice(0, DEFAULT_DISPLAY_ROW_LIMIT);

      return {
        columns,
        rows,
        displayRows,
        rowCount: result.numRows,
        executionTimeMs,
        sourceDatasetIds,
      };
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      throw new Error(`Query execution failed: ${errMsg}`);
    }
  }

  /**
   * Execute multiple SQL statements (separated by semicolons)
   * Returns the result of the last statement
   */
  async executeMultiStatement(
    sql: string,
    options: QueryExecutionOptions = {}
  ): Promise<QueryResult> {
    if (this.status !== 'ready' || !this.conn) {
      throw new Error('DuckDB is not ready. Current status: ' + this.status);
    }

    // Split by semicolons, filtering empty statements
    const statements = sql
      .split(';')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    if (statements.length === 0) {
      throw new Error('No valid SQL statements provided');
    }

    // Execute all statements, return result of last one
    let lastResult: QueryResult | null = null;
    for (const stmt of statements) {
      lastResult = await this.executeQuery(stmt, options);
    }

    return lastResult!;
  }

  /**
   * Register a table from Arrow data
   */
  async registerArrowTable(
    tableName: string,
    arrowTable: unknown,
    datasetId: string,
    isSecure: boolean = false
  ): Promise<void> {
    if (this.status !== 'ready' || !this.conn || !this.db) {
      throw new Error('DuckDB is not ready. Current status: ' + this.status);
    }

    try {
      // Register Arrow table with DuckDB
      await this.db.registerFileBuffer(
        `${tableName}.arrow`,
        new Uint8Array(arrowTable as ArrayBuffer)
      );

      // Create table from Arrow file
      await this.conn.query(`
        CREATE OR REPLACE TABLE "${tableName}" AS
        SELECT * FROM read_parquet('${tableName}.arrow')
      `);

      // Track in registry for lineage
      this.tableRegistry.set(tableName, { datasetId, isSecure });

      console.log(
        `[DuckDB] Registered table: ${tableName} (datasetId: ${datasetId}, secure: ${isSecure})`
      );
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to register table ${tableName}: ${errMsg}`);
    }
  }

  /**
   * Register a table directly from an Apache Arrow Table
   * This is the fast path - uses DuckDB's native Arrow insertion for near-instant loading
   */
  async insertArrowTableDirect(
    tableName: string,
    arrowTable: ArrowTable,
    datasetId: string,
    isSecure: boolean = false
  ): Promise<number> {
    if (this.status !== 'ready' || !this.conn) {
      throw new Error('DuckDB is not ready. Current status: ' + this.status);
    }

    try {
      // Drop existing table if any
      await this.conn.query(`DROP TABLE IF EXISTS "${tableName}"`);

      // Use Arrow IPC stream for reliable worker communication
      // The IPC stream serializes the Arrow table to bytes, which transfers
      // more reliably to the DuckDB worker than passing the Table object directly.
      // This is especially important in production builds where module boundaries
      // can cause issues with object references.
      // See: https://github.com/duckdb/duckdb-wasm/blob/main/packages/duckdb-wasm/README.md
      const ipcBuffer = tableToIPC(arrowTable, 'stream');
      await this.conn.insertArrowFromIPCStream(ipcBuffer, {
        name: tableName,
        schema: 'main',
        create: true,
      });

      // Verify the table was actually created
      const verifyResult = await this.conn.query(
        `SELECT COUNT(*) as cnt FROM information_schema.tables WHERE table_name = '${tableName}'`
      );
      const verifyRows = verifyResult.toArray();
      const tableExists = verifyRows.length > 0 && Number(verifyRows[0].cnt) > 0;

      if (!tableExists) {
        console.error(
          `[DuckDB] Table ${tableName} was not created after insertArrowFromIPCStream - Arrow table info:`,
          { numRows: arrowTable.numRows, numCols: arrowTable.numCols, schema: arrowTable.schema }
        );
        throw new Error(`Table ${tableName} was not created. Arrow IPC insertion failed.`);
      }

      // Track in registry for lineage
      this.tableRegistry.set(tableName, { datasetId, isSecure });

      const rowCount = arrowTable.numRows;
      console.log(
        `[DuckDB] Registered Arrow table: ${tableName} (${rowCount} rows, datasetId: ${datasetId}, secure: ${isSecure})`
      );
      return rowCount;
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to register Arrow table ${tableName}: ${errMsg}`);
    }
  }

  /**
   * Register a table from JSON data
   */
  async registerJsonTable(
    tableName: string,
    data: Record<string, unknown>[],
    datasetId: string,
    isSecure: boolean = false
  ): Promise<void> {
    if (this.status !== 'ready' || !this.conn) {
      throw new Error('DuckDB is not ready. Current status: ' + this.status);
    }

    if (!data || data.length === 0) {
      throw new Error('Cannot register empty table');
    }

    try {
      // Create table structure from first row
      const firstRow = data[0];
      const columns = Object.entries(firstRow).map(([key, value]) => {
        let type = 'VARCHAR';
        if (typeof value === 'number') {
          type = Number.isInteger(value) ? 'BIGINT' : 'DOUBLE';
        } else if (typeof value === 'boolean') {
          type = 'BOOLEAN';
        } else if (value instanceof Date) {
          type = 'TIMESTAMP';
        }
        return `"${key}" ${type}`;
      });

      // Drop existing table if any
      await this.conn.query(`DROP TABLE IF EXISTS "${tableName}"`);

      // Create table
      await this.conn.query(`CREATE TABLE "${tableName}" (${columns.join(', ')})`);

      // Insert data in batches
      const batchSize = 1000;
      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);
        const colNames = Object.keys(firstRow)
          .map(k => `"${k}"`)
          .join(', ');
        const values = batch.map(row => {
          const vals = Object.values(row).map(v => {
            if (v === null || v === undefined) return 'NULL';
            if (typeof v === 'string') return `'${v.replace(/'/g, "''")}'`;
            if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
            if (v instanceof Date) return `'${v.toISOString()}'`;
            // Handle objects (including arrays and nested objects like GeoJSON)
            if (typeof v === 'object') {
              const jsonStr = JSON.stringify(v).replace(/'/g, "''");
              return `'${jsonStr}'`;
            }
            return String(v);
          });
          return `(${vals.join(', ')})`;
        });

        await this.conn.query(
          `INSERT INTO "${tableName}" (${colNames}) VALUES ${values.join(', ')}`
        );
      }

      // Track in registry for lineage
      this.tableRegistry.set(tableName, { datasetId, isSecure });

      console.log(
        `[DuckDB] Registered JSON table: ${tableName} (${data.length} rows, datasetId: ${datasetId}, secure: ${isSecure})`
      );
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to register JSON table ${tableName}: ${errMsg}`);
    }
  }

  /**
   * Remove a registered table
   */
  async unregisterTable(tableName: string): Promise<void> {
    if (this.status !== 'ready' || !this.conn) {
      return; // Silently ignore if not ready
    }

    try {
      await this.conn.query(`DROP TABLE IF EXISTS "${tableName}"`);
      this.tableRegistry.delete(tableName);
      console.log(`[DuckDB] Unregistered table: ${tableName}`);
    } catch (error) {
      console.warn(`[DuckDB] Failed to unregister table ${tableName}:`, error);
    }
  }

  /**
   * Get list of all registered tables
   */
  async getRegisteredTables(): Promise<string[]> {
    if (this.status !== 'ready' || !this.conn) {
      return [];
    }

    try {
      const result = await this.conn.query(
        `SELECT table_name FROM information_schema.tables WHERE table_schema = 'main'`
      );
      return result
        .toArray()
        .map((row: Record<string, unknown>) => String(row['table_name'] || row[0]));
    } catch (error) {
      console.warn('[DuckDB] Failed to get table list:', error);
      return [];
    }
  }

  /**
   * Get table schema information
   */
  async getTableSchema(tableName: string): Promise<SqlColumn[]> {
    if (this.status !== 'ready' || !this.conn) {
      throw new Error('DuckDB is not ready');
    }

    try {
      const result = await this.conn.query(`DESCRIBE "${tableName}"`);
      return result.toArray().map((row: Record<string, unknown>) => ({
        name: String(row['column_name'] || row[0]),
        type: String(row['column_type'] || row[1]),
        nullable: row['null'] === 'YES' || row[2] === 'YES',
      }));
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to get schema for ${tableName}: ${errMsg}`);
    }
  }

  /**
   * Check if a query result contains data from secure sources
   */
  hasSecureSource(sourceDatasetIds: string[]): boolean {
    return sourceDatasetIds.some(id => {
      for (const [, info] of this.tableRegistry) {
        if (info.datasetId === id && info.isSecure) {
          return true;
        }
      }
      return false;
    });
  }

  /**
   * Extract dataset IDs from tables referenced in a SQL query
   */
  private getSourceDatasetIds(sql: string): string[] {
    const datasetIds = new Set<string>();
    const upperSql = sql.toUpperCase();

    // Check each registered table to see if it's referenced
    for (const [tableName, info] of this.tableRegistry) {
      // Security: tableName is from internal registry, already sanitized by sanitizeDuckDBTableName
      const tablePattern = new RegExp(`\\b${tableName.toUpperCase()}\\b`);
      if (tablePattern.test(upperSql)) {
        datasetIds.add(info.datasetId);
      }
    }

    return Array.from(datasetIds);
  }

  /**
   * Get the table registry entry for a table
   */
  getTableInfo(tableName: string): { datasetId: string; isSecure: boolean } | undefined {
    return this.tableRegistry.get(tableName);
  }

  /**
   * Close the database connection and clean up
   */
  async close(): Promise<void> {
    try {
      if (this.conn) {
        await this.conn.close();
        this.conn = null;
      }
      if (this.db) {
        await this.db.terminate();
        this.db = null;
      }
      this.tableRegistry.clear();
      this.status = 'uninitialized';
      console.log('[DuckDB] Service closed');
    } catch (error) {
      console.warn('[DuckDB] Error during close:', error);
    }
  }
}

export default DuckDBService;
